/**
 * Shared constants for the Recent Content Menu.
 *
 * These are extracted into a separate lightweight file to avoid pulling the
 * full recentQueryConfig module (which contains heavy query-building logic,
 * data transforms, and GraphQL queries) into chunks that only need these
 * constants (e.g. the preloader). This prevents tree-shaking defeats.
 */

import { PlatformSiteAri } from '@atlassian/ari';

import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv.ts';

import type { RecentContentMenuQuery$variables } from './queries/__generated__/RecentContentMenuQuery.graphql.ts';

export enum ActivityEventType {
	ASSIGNED = 'ASSIGNED',
	COMMENTED = 'COMMENTED',
	CREATED = 'CREATED',
	EDITED = 'EDITED',
	LIKED = 'LIKED',
	PUBLISHED = 'PUBLISHED',
	SNAPSHOTTED = 'SNAPSHOTTED',
	STARTED = 'STARTED',
	TRANSITIONED = 'TRANSITIONED',
	UNASSIGNED = 'UNASSIGNED',
	UPDATED = 'UPDATED',
	VIEWED = 'VIEWED',
}

export enum ActivityObjectType {
	PAGE = 'PAGE',
	BLOGPOST = 'BLOGPOST',
	WHITEBOARD = 'WHITEBOARD',
	DATABASE = 'DATABASE',
	SLIDE = 'SLIDE',
	EMBED = 'EMBED',
	CONTENT = 'CONTENT',
	COMMENT = 'COMMENT',
}

export const PAGE_SIZE = 30;

/** Full set of activity object types; callers drop SLIDE when `confluence-slides` is off. */
export const OBJECT_TYPES: ActivityObjectType[] = [
	ActivityObjectType.PAGE,
	ActivityObjectType.BLOGPOST,
	ActivityObjectType.WHITEBOARD,
	ActivityObjectType.DATABASE,
	ActivityObjectType.SLIDE,
	ActivityObjectType.EMBED,
	ActivityObjectType.CONTENT,
];

/** Object types for recent activity queries, omitting {@link ActivityObjectType.SLIDE} when slides are disabled in this environment. */
export function objectTypes(): ActivityObjectType[] {
	return OBJECT_TYPES.filter(
		(t) => t !== ActivityObjectType.SLIDE || isContentTypeEnabledInCurrentEnv('slide'),
	);
}

export const WORKED_ON_EVENT_TYPES: ActivityEventType[] = [
	ActivityEventType.EDITED,
	ActivityEventType.PUBLISHED,
	ActivityEventType.UPDATED,
	ActivityEventType.CREATED,
	ActivityEventType.STARTED,
	ActivityEventType.SNAPSHOTTED,
];

/** Teamwork Graph product and object types for third-party (3P) items: files, smart links, pull requests and work items. */
const GRAPH_ACTIVITY_PRODUCT = 'graph';
const GRAPH_OBJECT_TYPES = ['document', 'remote-link', 'pull-request', 'work-item'];

/**
 * Teamwork Graph activity: third-party ARIs, which AGG hydrates as `External*` data, e.g.
 * "ari:third-party:google.google-drive::document/1", and graph ARIs, which have no 3P data and are dropped.
 */
export const isThirdPartyActivity = (objectId?: string | null): boolean =>
	/^ari:(third-party|cloud:graph):/.test(objectId ?? '');

type ThirdPartyActivityData = {
	displayName?: string | null;
	url?: string | null;
	thirdPartyId?: string | null;
	provider?: { name?: string | null } | null;
};

type ThirdPartyItem = {
	title: string;
	url: string;
	thirdPartyId?: string | null;
	providerName: string;
};

/**
 * What a 3P Recent item shows, from its `External*` data. Without a title, URL or provider there is
 * nothing to show, so it's null and the item is dropped.
 */
export const getThirdPartyItem = (data?: ThirdPartyActivityData | null): ThirdPartyItem | null =>
	data?.displayName && data.url && data.provider?.name
		? {
				title: data.displayName,
				url: data.url,
				thirdPartyId: data.thirdPartyId,
				// Provider names can end in " provider" (e.g. "Google Drive provider"), as create-space's
				// connectorFiles.ts also handles.
				providerName: data.provider.name.replace(/\s+provider$/i, ''),
			}
		: null;

/**
 * Event types for the "All" filter when it includes 3P items. `eventTypes` applies to every
 * product in the query, so this is Quick Find's list (which covers Confluence events) plus `clicked`,
 * without `sl-viewed`: smart link views are a noisy signal that would drown out the list.
 */
const RECENT_WITH_3P_EVENT_TYPES = [
	'assigned',
	'unassigned',
	'viewed',
	'clicked',
	'updated',
	'created',
	'liked',
	'transitioned',
	'published',
	'edited',
	'commented',
	'sl-clicked',
	'shared',
	'exported',
	'deleted',
	'started',
	'snapshotted',
	'trashed',
	'reaction',
];

/**
 * Confluence's "Worked on" event types for queries that include 3P items, lowercase like the "All"
 * list. They include `created` and `updated`, which the activity service counts as worked on for 3P
 * items.
 */
export const WORKED_ON_WITH_3P_EVENT_TYPES: string[] = [
	'edited',
	'published',
	'updated',
	'created',
	'started',
	'snapshotted',
];

/** Variables that add 3P items to a Recent query, next to its Confluence object types. */
export const getThirdPartyRecentVars = (
	confluenceObjectTypes: string[],
	eventTypes: string[] = RECENT_WITH_3P_EVENT_TYPES,
): { products: string[]; eventTypes: string[]; objectTypes: string[] } => ({
	products: ['CONFLUENCE', GRAPH_ACTIVITY_PRODUCT],
	eventTypes,
	objectTypes: [...confluenceObjectTypes, ...GRAPH_OBJECT_TYPES],
});

/**
 * Variables for the "All" filter. The dialog and the preloader both use them, so the preloaded result
 * is reused. Each caller reads the 3P experiment itself: the preloader without exposure.
 */
export function getAllRecentInitialVars(
	cloudId: string,
	include3P: boolean,
): RecentContentMenuQuery$variables {
	return {
		cloudId: PlatformSiteAri.create({ siteId: cloudId }).toString(),
		after: '',
		first: PAGE_SIZE,
		...(include3P
			? getThirdPartyRecentVars(objectTypes())
			: {
					eventTypes: [],
					objectTypes: objectTypes(),
				}),
	};
}
